import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import i18n from './i18n';
import './index.css';
import App from './App.js';
import { ToastProvider, notifyToast } from './components/ui/Toast.js';
import { ApiError } from './lib/api.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { esModuloQueYaNoEsta, limpiarMarcaDeRecarga, recargarUnaVez } from './lib/moduloDinamico.js';

// ── Una pestaña vieja contra una build nueva ─────────────────────────────────
// Arrancamos: si veníamos de una recarga por módulo faltante, la marca ya
// cumplió su función y se limpia — la próxima versión vuelve a poder recargar.
limpiarMarcaDeRecarga();

// Vite avisa por acá cuando un chunk precargado no se puede traer. Es el mismo
// caso que atiende cargarModulo(): no es un error para mostrar, es una build
// que cambió abajo de los pies.
window.addEventListener('vite:preloadError', event => {
  event.preventDefault();
  recargarUnaVez();
});

window.addEventListener('unhandledrejection', event => {
  if (esModuloQueYaNoEsta(event.reason)) {
    event.preventDefault();
    recargarUnaVez();
  }
});

// ── Errores de mutación: se muestran SIEMPRE, por defecto ────────────────────
// Bug de clase, no puntual (2026-08-18): un formulario que llama mutate() sin
// onError deja el error muriendo en la promesa — el usuario aprieta "guardar",
// no pasa nada visible, y cree que la app está rota. Pasó dos veces seguidas
// (conectar casilla, datos de facturación) y un barrido encontró 42 llamadas
// sin manejo en el resto de la app.
//
// Parchear las 42 no evita la número 43. Esto invierte el default: cualquier
// mutación que falle avisa, sin que cada formulario tenga que acordarse. Un
// formulario que necesite tratar su error distinto define su propio onError y
// acá se lo respeta (no se duplica el aviso).
const mutationCache = new MutationCache({
  onError: (error, _vars, _ctx, mutation) => {
    if (mutation.options.onError) return; // lo maneja el caller
    const msg = error instanceof ApiError
      ? error.message
      : (error instanceof Error ? error.message : String(error));
    notifyToast({ message: msg, tone: 'error' });
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

// Al cambiar de idioma, todo lo que el servidor escribió en el otro se vuelve
// a pedir: los hallazgos, los motivos y los avisos salen en el idioma nuevo.
i18n.on('languageChanged', () => { void queryClient.invalidateQueries(); });

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ToastProvider>
            <App />
          </ToastProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
